import AddIcon from '@mui/icons-material/Add';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Divider,
    InputAdornment,
    List,
    ListItemButton,
    TextField,
    Typography,
} from '@mui/material';
import { useAuth } from '../../auth/AuthContext';
import type { EntitySpec } from '../../models/Models';
import { COUNTRY_OPTIONS } from '../../models/countries';
import { displayName } from './helpers';
import type { AnyEntity } from './types';

interface SidebarProps {
  spec: EntitySpec;
  items: AnyEntity[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onNew: () => void;
  search: string;
  onSearchChange: (v: string) => void;
  countries: string[];
  onCountriesChange: (v: string[]) => void;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  extraAction?: React.ReactNode;
}

const SidebarItem = ({
  item,
  spec,
  isSelected,
  onClick,
}: {
  item: AnyEntity;
  spec: EntitySpec;
  isSelected: boolean;
  onClick: () => void;
}) => {
  const subtitle = (item.headquarters ?? item.country ?? item.description ?? spec.singular) as string;
  return (
    <ListItemButton
      selected={isSelected}
      onClick={onClick}
      sx={{ flexDirection: 'column', alignItems: 'flex-start', py: 1, px: 1.5 }}
    >
      <Typography
        sx={{
          fontWeight: isSelected ? 700 : 600,
          fontSize: '0.88rem',
          color: isSelected ? '#0087C3' : '#002A5C',
          lineHeight: 1.3,
        }}
      >
        {displayName(item, spec.singular)}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          color: '#8E99A8',
          fontSize: '0.72rem',
          mt: 0.25,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '100%',
        }}
      >
        {subtitle}
      </Typography>
    </ListItemButton>
  );
};

const EmptyResults = () => (
  <Box sx={{ p: 4, textAlign: 'center' }}>
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: '12px',
        bgcolor: '#E8F4FD',
        color: '#0087C3',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 1,
      }}
    >
      <SearchIcon />
    </Box>
    <Typography variant="body2" sx={{ color: '#5C6B7A', fontWeight: 600 }}>
      No items found
    </Typography>
    <Typography variant="caption" sx={{ color: '#8E99A8' }}>
      Try a different search term
    </Typography>
  </Box>
);

const Sidebar = ({
  spec,
  items,
  selectedId,
  onSelect,
  onNew,
  search,
  onSearchChange,
  countries,
  onCountriesChange,
  isLoading,
  isError,
  error,
  onRetry,
  extraAction,
}: SidebarProps) => {
  const { can } = useAuth();

  return (
  <Card sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <TextField
        size="small"
        placeholder="Search by keyword"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#8E99A8' }} />
              </InputAdornment>
            ),
          },
        }}
      />
      {spec.searchByCountry && (
        <Autocomplete
          multiple
          size="small"
          options={COUNTRY_OPTIONS}
          value={COUNTRY_OPTIONS.filter((o) => countries.includes(o.value))}
          onChange={(_, value) => onCountriesChange(value.map((v) => v.value))}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          disableCloseOnSelect
          renderValue={(value, getItemProps) =>
            value.map((opt, idx) => (
              <Chip
                {...getItemProps({ index: idx })}
                key={opt.value}
                label={opt.value}
                size="small"
                sx={{ height: 22 }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={countries.length ? '' : 'Search by country'}
              slotProps={{
                ...params.slotProps,
                input: {
                  ...params.slotProps.input,
                  startAdornment: (
                    <>
                      <InputAdornment position="start" sx={{ ml: 0.5 }}>
                        <PublicOutlinedIcon sx={{ fontSize: 18, color: '#8E99A8' }} />
                      </InputAdornment>
                      {params.slotProps.input.startAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#5C6B7A', fontWeight: 600 }}>
          {items.length} item{items.length === 1 ? '' : 's'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
          {extraAction}
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={onNew} sx={{ display: can('create') ? undefined : 'none' }}>
            New
          </Button>
        </Box>
      </Box>
    </Box>
    <Divider />
    <Box sx={{ overflow: 'auto', flex: 1, py: 0.5 }}>
      {isLoading && (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={20} />
        </Box>
      )}
      {isError && (
        <Box sx={{ p: 2 }}>
          <Typography color="error" variant="body2">
            {(error as Error)?.message ?? 'Failed to load'}
          </Typography>
          <Button size="small" onClick={onRetry} sx={{ mt: 1 }}>
            Retry
          </Button>
        </Box>
      )}
      <List dense disablePadding>
        {items.map((item) => (
          <SidebarItem
            key={item.id}
            item={item}
            spec={spec}
            isSelected={item.id === selectedId}
            onClick={() => onSelect(item.id)}
          />
        ))}
        {!isLoading && items.length === 0 && <EmptyResults />}
      </List>
    </Box>
  </Card>
  );
};

export default Sidebar;
